import React, { useState } from 'react';

import Tooltip from '@mui/material/Tooltip';
import TextFieldMaterial, { TextFieldProps } from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import classnames from 'classnames';
import ErrorMessage from 'components/ErrorMessage/index';
import COLORS from 'consts/colors';

const useStyles = makeStyles(() => ({
  textField: {
    '& div.MuiInputBase-root.Mui-focused': {
      '& fieldset': {
        border: `2px solid ${COLORS.BC_DARK_GREEN} !important`,
      },
    },
    '& div.MuiInputBase-root.Mui-error': {
      '& fieldset': {
        border: `2px solid ${COLORS.BC_RED} !important`,
      },
    },
    '& label.MuiInputLabel-root': {
      fontSize: '14px',
      color: COLORS.TEXT,
    },
    '& textarea': {
      fontSize: '14px',
      color: COLORS.TEXT,
    },
    '& input': {
      fontSize: '14px',
      color: COLORS.TEXT,
    },
    '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
      backgroundColor: 'unset',
      '& p': {
        fontSize: '14px',
        color: COLORS.TEXT,
      },
    },
    '& div': {
      backgroundColor: COLORS.WHITE,
    },
    '& fieldset': {
      borderColor: `${COLORS.BORDER_TEXTFIELD} !important`,
      borderWidth: '1px !important',
    },
    '& div.Mui-disabled': {
      backgroundColor: COLORS.DISABLED_BACKGROUND_AUTOCOMPLETE,

      '& textarea': {
        '-webkit-text-fill-color': COLORS.TEXT_DISABLED,
        color: COLORS.TEXT_DISABLED,
      },
      '& input': {
        '-webkit-text-fill-color': COLORS.TEXT_DISABLED,
        color: COLORS.TEXT_DISABLED,
      },
      '& .MuiAutocomplete-endAdornment': {
        display: 'none',
      },
    },
  },
}));

export type ITextFieldProps = TextFieldProps & {
  showTooltip?: boolean;
  errorMessage?: string;
};

const TextField = React.forwardRef((props: ITextFieldProps, ref?: React.Ref<any>) => {
  const classes = useStyles();
  const { className, disabled, errorMessage, error, value, showTooltip, onMouseEnter, InputProps, ...restProps } =
    props;

  const [open, setOpen] = useState(false);

  const handleShowTooltip = () => {
    if (value && showTooltip) {
      setOpen(true);
    }
  };

  const handleCloseTooltip = () => {
    if (value && showTooltip && open) {
      setOpen(false);
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    onMouseEnter && onMouseEnter(event);
    handleShowTooltip();
  };

  if (ref)
    return (
      <>
        <Tooltip title={value && showTooltip ? (value as string) : ''} onClose={handleCloseTooltip} open={open}>
          <TextFieldMaterial
            {...restProps}
            autoComplete="new-password"
            className={classnames(classes.textField, {
              [className || '']: !!className,
            })}
            inputRef={ref}
            value={value}
            disabled={disabled}
            InputProps={{
              ...InputProps,
              spellCheck: false,
              sx: {
                borderRadius: '0px !important',
                '&: hover': !disabled
                  ? {
                      borderColor: COLORS.BC_GREEN,
                    }
                  : undefined,
              },
            }}
            error={!!errorMessage || !!error}
            onMouseEnter={handleMouseEnter}
          />
        </Tooltip>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </>
    );
  return (
    <>
      <Tooltip title={value && showTooltip ? (value as string) : ''} onClose={handleCloseTooltip} open={open}>
        <TextFieldMaterial
          color="success"
          minRows={4}
          {...restProps}
          autoComplete="new-password"
          className={classnames(classes.textField, {
            [className || '']: !!className,
          })}
          value={value}
          disabled={disabled}
          InputProps={{
            ...InputProps,
            spellCheck: false,
            sx: {
              borderRadius: '0px !important',
              '&: hover': !disabled
                ? {
                    borderColor: COLORS.BC_GREEN,
                  }
                : undefined,
            },
          }}
          error={!!errorMessage || !!error}
          onMouseEnter={handleMouseEnter}
        />
      </Tooltip>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </>
  );
});

TextField.defaultProps = {
  size: 'small',
  fullWidth: true,
  color: 'success',
  variant: 'outlined',
  error: false,
};

export default TextField;
